import { useState, useEffect } from "react";
import InputBase from "./InputBase";
import { twMerge } from "tailwind-merge";
import { EyeSlashIcon } from "@heroicons/react/24/outline";

type SensitiveTextareaProps = {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  required: boolean;
  error?: string;
  icon?: React.ReactNode | null;
  helperText?: string;
  inputClassName?: string;
  disabled?: boolean;
}

const SensitiveTextarea = ({
  label,
  placeholder,
  value,
  onChange,
  required,
  error = '',
  icon = null,
  helperText = '',
  inputClassName = '',
  disabled = false,
}: SensitiveTextareaProps) => {
  const [ isVisible, setIsVisible ] = useState(false);

  const valueAsString = value || '';

  const displayValue = !isVisible
    ? value
      ? value.split('\n').map(line => "*".repeat(line.length)).join('\n')
      : ''
    : valueAsString;

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isVisible) {
      return;
    }
    const newValue = e.target.value;
    onChange(newValue);
  };

  const handleOverlayClick = () => {
    if (!disabled) {
      setIsVisible(true);
    }
  };

  const handleDone = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (disabled) {
      setIsVisible(false);
    }
  }, [disabled]);

  return (
    <InputBase label={label} required={required} icon={icon} helperText={helperText} error={error}>
      <div className="relative">
        <div className="relative group">
          <textarea
            id={`${label}-textarea`}
            required={required}
            value={displayValue}
            rows={10}
            placeholder={placeholder}
            onChange={handleChange}
            disabled={disabled}
            className={twMerge(
              `w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 resize-y transition-colors duration-150 ${
                error
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : (disabled || !isVisible)
                  ? 'border-gray-300 bg-gray-100 text-gray-400'
                  : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500'
              } ${disabled || !isVisible ? 'cursor-not-allowed' : 'cursor-text'}`,
              inputClassName
            )}
          />
          {!isVisible && !disabled && (
            <div
              className="pointer-events-auto absolute inset-0 flex items-center justify-center z-10 group-hover:opacity-100 opacity-80 bg-gray-100 border-2 border-dashed border-gray-400 rounded-lg transition-opacity cursor-pointer"
              tabIndex={0}
              onClick={handleOverlayClick}
              role="button"
              aria-label="Click to view or edit"
            >
              <span className="text-sm text-gray-500 select-none px-2 py-1 bg-white bg-opacity-80 rounded shadow-sm">
                Click to View or Edit
              </span>
            </div>
          )}
        </div>
        {isVisible && !disabled && (
          <button
            type="button"
            onClick={handleDone}
            disabled={disabled}
            className="absolute right-8 top-3 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
            aria-label="Hide Text"
          >
            <EyeSlashIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </InputBase>
  );
};

export default SensitiveTextarea;
